import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { QreVarianceYoyService } from './qre-variance-yoy.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-qre-variance-yoy',
    templateUrl: './qre-variance-yoy.component.html',
    styleUrls: ['./qre-variance-yoy.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class QreVarianceYoyComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  gridData: any = {};

  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  modalTitle = 'View YoY & Sign Off';
  entityList: { key: string; value: any }[] = [];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private qreVarianceYoyService: QreVarianceYoyService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(rawRow)) {
      this.gridData = rawRow;
      this.rowData = rawRow[0]?.data || rawRow[0] || {};
    } else {
      this.rowData = rawRow;
      this.gridData = rawRow;
    }
    this.cdr.detectChanges();
  }

  private tryInit() {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init() {
    this.buildEntityList();
    this.loadData();
  }

  private buildEntityList() {
    const r = this.rowData;
    this.entityList = [
      { key: 'CY_LEID', value: r.CY_LEID ?? r.cy_leid ?? r.LEID ?? r.leid },
      { key: 'CY_TAX_CODE', value: r.CY_TAX_CODE ?? r.cy_tax_code ?? r.TAX_CODE ?? r.tax_code },
      { key: 'LE_NAME', value: r.LE_NAME ?? r.le_name },
      { key: 'CY_REPORTING_PERIOD', value: r.CY_REPORTING_PERIOD ?? r.cy_reporting_period ?? r.REPORTING_PERIOD ?? r.reporting_period }
    ];
    this.cdr.detectChanges();
  }

  private getLoadParams(): Record<string, any> {
    const g = this.globalService?.globalParams || {};
    const f = this.filterParams || {};
    return {
      tax_year: this.rowData.TAX_YEAR ?? this.rowData.CY_TAX_YEAR ?? f.tax_year ?? g.tax_year,
      scenario: this.rowData.SCENARIO ?? f.scenario ?? g.scenario,
      leid: this.rowData.LEID ?? this.rowData.CY_LEID ?? this.rowData.leid ?? this.rowData.cy_leid,
      ...f,
      ...g
    };
  }

  private loadData() {
    this.showFormLoader = true;
    this.cdr.detectChanges();

    const params = this.getLoadParams();
    this.qreVarianceYoyService.loadData(this.GENERAL_CONFIG.base_url, '31jsr3', params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
      () => {
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  save() {
    if (this.crudLoading || this.isSaveClicked) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const jsonObj: any[] = [];
    const jsonSettings: any[] = [];

    this.qreVarianceYoyService.saveData(
      this.GENERAL_CONFIG.base_url,
      'usumyc',
      jsonObj,
      jsonSettings,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        if (result?.callSuccess !== '1') {
          this.gtwSnackbarService.openSnackBar(result?.errorMessage || 'Error saving data', 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Data has been saved successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  reset() {
    this.isInitialized = false;
    this.showFormLoader = true;
    this.tryInit();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
